import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, Loader2, Search, Car, User, Trash2, Calendar, DollarSign, CreditCard, FileText } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeader, InputDecimal, Modal, ConfirmDialog } from '../../components/ui';
import { Panel, Badge, buttonClass } from '../../design-system';
import { formatCurrency, formatDate } from '../../utils/format';

// Helper para converter valores inválidos em null
const numericOrNull = z.preprocess(
  (val) => {
    if (val === '' || val === null || val === undefined) return null;
    const num = Number(val);
    return isNaN(num) || num === 0 ? null : num;
  },
  z.number().nullable()
);

const vendaSchema = z.object({
  clienteId: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? null : Number(val)),
    z.number({ required_error: 'Selecione o cliente', invalid_type_error: 'Selecione o cliente' })
      .min(1, 'Selecione o cliente')
  ),
  veiculoId: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? null : Number(val)),
    z.number({ required_error: 'Selecione o veículo', invalid_type_error: 'Selecione o veículo' })
      .min(1, 'Selecione o veículo')
  ),
  // vendedorId: 0 significa "usar o usuário logado" no backend
  vendedorId: z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) return 0;
      const num = Number(val);
      return isNaN(num) ? 0 : num;
    },
    z.number().default(0)
  ),
  valorVenda: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? 0 : Number(val)),
    z.number({ required_error: 'Informe o valor', invalid_type_error: 'Valor inválido' })
      .min(0.01, 'Informe o valor da venda')
  ),
  valorDesconto: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? 0 : Number(val)),
    z.number().default(0)
  ),
  valorAcrescimo: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? 0 : Number(val)),
    z.number().default(0)
  ),
  veiculoTrocaId: numericOrNull,
  valorTroca: numericOrNull,
  formaPagamento: z.string().default('AVISTA'),
  valorEntrada: numericOrNull,
  quantidadeParcelas: numericOrNull,
  valorParcela: numericOrNull,
  financeira: z.string().optional(),
  comissaoPercentual: numericOrNull,
  observacoes: z.string().optional(),
  dataVenda: z.string().optional(),
});

type VendaFormData = z.infer<typeof vendaSchema>;

interface Veiculo {
  id: number;
  marcaNome: string;
  modeloNome: string;
  versao: string | null;
  ano: number;
  anoModelo: number;
  placa: string | null;
  valorVenda: number;
}

interface Cliente {
  id: number;
  nome: string;
  cpfCnpj: string | null;
  celular: string | null;
}

interface VendaDetail {
  id: number;
  clienteId: number;
  clienteNome: string;
  veiculoId: number;
  veiculoNome: string;
  veiculoPlaca: string | null;
  vendedorId: number;
  vendedorNome: string;
  valorVenda: number;
  valorDesconto: number;
  valorAcrescimo: number;
  valorFinal: number;
  veiculoTrocaId: number | null;
  veiculoTrocaNome: string | null;
  valorTroca: number | null;
  formaPagamento: string;
  valorEntrada: number | null;
  quantidadeParcelas: number | null;
  valorParcela: number | null;
  financeira: string | null;
  comissaoPercentual: number | null;
  comissaoValor: number | null;
  contratoGerado: boolean;
  dutTransferido: boolean;
  dataEntrega: string | null;
  observacoes: string | null;
  dataVenda: string;
  dataCadastro: string;
}

const formaPagamentoLabels: Record<string, string> = {
  AVISTA: 'À Vista',
  FINANCIAMENTO: 'Financiamento',
  CONSORCIO: 'Consórcio',
  CARTAO: 'Cartão',
  BOLETO: 'Boleto',
  PIX: 'PIX',
};

export function VendaForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, showApiError } = useNotification();
  const isEditing = Boolean(id);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditing);
  const [venda, setVenda] = useState<VendaDetail | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showVeiculoModal, setShowVeiculoModal] = useState(false);
  const [showClienteModal, setShowClienteModal] = useState(false);
  const [showTrocaModal, setShowTrocaModal] = useState(false);

  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [buscaVeiculo, setBuscaVeiculo] = useState('');
  const [buscaCliente, setBuscaCliente] = useState('');

  const [selectedVeiculo, setSelectedVeiculo] = useState<Veiculo | null>(null);
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [selectedTroca, setSelectedTroca] = useState<Veiculo | null>(null);

  // Carregar venda existente
  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();

    setIsLoading(true);
    api.get<VendaDetail>(`/vendas/${id}`, { signal: controller.signal })
      .then((response) => {
        setVenda(response.data);
      })
      .catch((error) => {
        if (error.name !== 'CanceledError') {
          showError('Erro ao carregar venda');
          navigate('/vendas');
        }
      })
      .finally(() => setIsLoading(false));

    return () => controller.abort();
  }, [id, navigate, showError]);

  // Handler para excluir venda
  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await api.delete(`/vendas/${id}`);
      showSuccess('Venda excluída com sucesso');
      navigate('/vendas');
    } catch (error) {
      showApiError(error);
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<VendaFormData>({
    resolver: zodResolver(vendaSchema),
    defaultValues: {
      formaPagamento: 'AVISTA',
      valorDesconto: 0,
      valorAcrescimo: 0,
      dataVenda: new Date().toISOString().split('T')[0],
    },
  });

  const formaPagamento = watch('formaPagamento');
  const valorVenda = watch('valorVenda') || 0;
  const valorDesconto = watch('valorDesconto') || 0;
  const valorAcrescimo = watch('valorAcrescimo') || 0;
  const valorTroca = watch('valorTroca') || 0;

  const valorFinal = valorVenda - valorDesconto + valorAcrescimo - valorTroca;

  // Buscar veículos disponíveis
  const buscarVeiculos = async (query: string = '') => {
    try {
      const params = new URLSearchParams({ status: 'DISPONIVEL', pageSize: '10' });
      if (query) params.set('busca', query);
      const response = await api.get<{ items: Veiculo[] }>(`/veiculos?${params}`);
      setVeiculos(response.data.items);
    } catch (error) {
      showApiError(error);
    }
  };

  // Buscar clientes (apenas tipo CLIENTE, não fornecedores)
  const buscarClientes = async (query: string = '') => {
    try {
      const params = new URLSearchParams({ pageSize: '10', tipo: 'CLIENTE' });
      if (query) params.set('busca', query);
      const response = await api.get<{ items: Cliente[] }>(`/pessoas?${params}`);
      setClientes(response.data.items);
    } catch (error) {
      showApiError(error);
    }
  };

  // Buscar veículos com debounce
  useEffect(() => {
    if (!showVeiculoModal && !showTrocaModal) return;

    // Buscar imediatamente se não houver busca ou se o modal acabou de abrir
    const timer = setTimeout(() => {
      buscarVeiculos(buscaVeiculo);
    }, buscaVeiculo ? 300 : 0);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscaVeiculo, showVeiculoModal, showTrocaModal]);

  // Buscar clientes com debounce
  useEffect(() => {
    if (!showClienteModal) return;

    const timer = setTimeout(() => {
      buscarClientes(buscaCliente);
    }, buscaCliente ? 300 : 0);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscaCliente, showClienteModal]);

  const handleSelectVeiculo = (veiculo: Veiculo) => {
    setSelectedVeiculo(veiculo);
    setValue('veiculoId', veiculo.id);
    setValue('valorVenda', veiculo.valorVenda);
    setShowVeiculoModal(false);
  };

  const handleSelectCliente = (cliente: Cliente) => {
    setSelectedCliente(cliente);
    setValue('clienteId', cliente.id);
    setShowClienteModal(false);
  };

  const handleSelectTroca = (veiculo: Veiculo) => {
    setSelectedTroca(veiculo);
    setValue('veiculoTrocaId', veiculo.id);
    setShowTrocaModal(false);
  };

  const onSubmit = async (data: VendaFormData) => {
    setIsSaving(true);
    try {
      await api.post('/vendas', data);
      showSuccess('Venda registrada com sucesso');
      navigate('/vendas');
    } catch (error) {
      showApiError(error);
    } finally {
      setIsSaving(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Detail view for existing sale
  if (isEditing && venda) {
    return (
      <div>
        <PageHeader
          title="Detalhes da Venda"
          subtitle={`Venda #${venda.id} - ${formatDate(venda.dataVenda)}`}
          showBack
          backTo="/vendas"
        >
          <button
            onClick={() => setShowDeleteDialog(true)}
            className={buttonClass('danger')}
          >
            <Trash2 className="w-4 h-4" />
            Excluir Venda
          </button>
        </PageHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Coluna Principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Veículo */}
            <Panel title="Veículo" icon={<Car className="h-4 w-4" />}>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-lg font-medium text-slate-900">{venda.veiculoNome}</p>
                {venda.veiculoPlaca && (
                  <p className="font-mono text-sm text-slate-500">{venda.veiculoPlaca}</p>
                )}
              </div>
            </Panel>

            {/* Cliente */}
            <Panel title="Cliente" icon={<User className="h-4 w-4" />}>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-lg font-medium text-slate-900">{venda.clienteNome}</p>
              </div>
            </Panel>

            {/* Valores */}
            <Panel title="Valores" icon={<DollarSign className="h-4 w-4" />}>
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b border-slate-100 py-2">
                  <span className="text-slate-600">Valor do Veículo</span>
                  <span className="font-medium tabular-nums">{formatCurrency(venda.valorVenda)}</span>
                </div>
                {venda.valorDesconto > 0 && (
                  <div className="flex items-center justify-between border-b border-slate-100 py-2">
                    <span className="text-slate-600">Desconto</span>
                    <span className="font-medium tabular-nums text-rose-600">- {formatCurrency(venda.valorDesconto)}</span>
                  </div>
                )}
                {venda.valorAcrescimo > 0 && (
                  <div className="flex items-center justify-between border-b border-slate-100 py-2">
                    <span className="text-slate-600">Acréscimo</span>
                    <span className="font-medium tabular-nums text-emerald-600">+ {formatCurrency(venda.valorAcrescimo)}</span>
                  </div>
                )}
                {venda.valorTroca && venda.valorTroca > 0 && (
                  <div className="flex items-center justify-between border-b border-slate-100 py-2">
                    <span className="text-slate-600">Veículo de Troca</span>
                    <span className="font-medium tabular-nums text-slate-700">- {formatCurrency(venda.valorTroca)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between rounded-xl bg-emerald-50 px-4 py-3">
                  <span className="font-semibold text-slate-900">Valor Final</span>
                  <span className="text-xl font-bold tabular-nums text-emerald-600">{formatCurrency(venda.valorFinal)}</span>
                </div>
              </div>
            </Panel>

            {/* Veículo de Troca */}
            {venda.veiculoTrocaId && (
              <Panel title="Veículo de Troca" icon={<Car className="h-4 w-4" />}>
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="font-medium text-slate-900">{venda.veiculoTrocaNome}</p>
                  <p className="mt-1 text-sm font-medium tabular-nums text-slate-600">
                    Valor: {formatCurrency(venda.valorTroca || 0)}
                  </p>
                </div>
              </Panel>
            )}

            {/* Observações */}
            {venda.observacoes && (
              <Panel title="Observações" icon={<FileText className="h-4 w-4" />}>
                <p className="whitespace-pre-wrap text-slate-700">{venda.observacoes}</p>
              </Panel>
            )}
          </div>

          {/* Coluna Lateral */}
          <div className="space-y-6">
            {/* Informações da Venda */}
            <Panel title="Informações">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500">Data da Venda</p>
                  <p className="flex items-center gap-2 font-medium">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    {formatDate(venda.dataVenda)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Vendedor</p>
                  <p className="font-medium">{venda.vendedorNome}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Forma de Pagamento</p>
                  <p className="flex items-center gap-2 font-medium">
                    <CreditCard className="w-4 h-4 text-slate-400" />
                    {formaPagamentoLabels[venda.formaPagamento] || venda.formaPagamento}
                  </p>
                </div>
                {venda.valorEntrada && venda.valorEntrada > 0 && (
                  <div>
                    <p className="text-sm text-slate-500">Entrada</p>
                    <p className="font-medium tabular-nums">{formatCurrency(venda.valorEntrada)}</p>
                  </div>
                )}
                {venda.quantidadeParcelas && venda.quantidadeParcelas > 0 && (
                  <div>
                    <p className="text-sm text-slate-500">Parcelamento</p>
                    <p className="font-medium tabular-nums">
                      {venda.quantidadeParcelas}x de {formatCurrency(venda.valorParcela || 0)}
                    </p>
                  </div>
                )}
                {venda.financeira && (
                  <div>
                    <p className="text-sm text-slate-500">Financeira</p>
                    <p className="font-medium">{venda.financeira}</p>
                  </div>
                )}
                {venda.comissaoPercentual && (
                  <div>
                    <p className="text-sm text-slate-500">Comissão</p>
                    <p className="font-medium tabular-nums">
                      {venda.comissaoPercentual}% ({formatCurrency(venda.comissaoValor || 0)})
                    </p>
                  </div>
                )}
              </div>
            </Panel>

            {/* Status */}
            <Panel title="Status">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Contrato Gerado</span>
                  <Badge tone={venda.contratoGerado ? 'success' : 'neutral'}>
                    {venda.contratoGerado ? 'Sim' : 'Não'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">DUT Transferido</span>
                  <Badge tone={venda.dutTransferido ? 'success' : 'neutral'}>
                    {venda.dutTransferido ? 'Sim' : 'Não'}
                  </Badge>
                </div>
                {venda.dataEntrega && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Data Entrega</span>
                    <span className="font-medium">{formatDate(venda.dataEntrega)}</span>
                  </div>
                )}
              </div>
            </Panel>
          </div>
        </div>

        <ConfirmDialog
          isOpen={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={handleDelete}
          title="Excluir Venda"
          message="Tem certeza que deseja excluir esta venda? O veículo voltará ao status disponível. Esta ação não pode ser desfeita."
          type="danger"
          confirmText="Excluir"
          isLoading={isDeleting}
        />
      </div>
    );
  }

  // Create form
  return (
    <div>
      <PageHeader
        title="Nova Venda"
        subtitle="Registre uma nova venda"
        showBack
        backTo="/vendas"
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Veículo e Cliente */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Veículo */}
          <Panel title="Veículo">
            {selectedVeiculo ? (
              <div className="rounded-xl bg-slate-50 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="grid h-12 w-12 place-items-center rounded-xl bg-slate-100 text-slate-400 ring-1 ring-slate-200">
                      <Car className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {selectedVeiculo.marcaNome} {selectedVeiculo.modeloNome}
                      </p>
                      <p className="text-sm text-slate-500">
                        {selectedVeiculo.versao && `${selectedVeiculo.versao} • `}
                        {selectedVeiculo.ano}/{selectedVeiculo.anoModelo}
                        {selectedVeiculo.placa && ` • ${selectedVeiculo.placa}`}
                      </p>
                      <p className="text-sm font-medium tabular-nums text-emerald-600">
                        {formatCurrency(selectedVeiculo.valorVenda)}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowVeiculoModal(true)}
                    className="text-sm font-semibold text-primary-600 hover:text-primary-700"
                  >
                    Alterar
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowVeiculoModal(true)}
                className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-4 text-slate-500 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <Car className="w-5 h-5" />
                Selecionar Veículo
              </button>
            )}
            {errors.veiculoId && (
              <p className="mt-2 text-sm text-rose-600">{errors.veiculoId.message}</p>
            )}
          </Panel>

          {/* Cliente */}
          <Panel title="Cliente">
            {selectedCliente ? (
              <div className="rounded-xl bg-slate-50 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="grid h-12 w-12 place-items-center rounded-xl bg-slate-100 text-slate-400 ring-1 ring-slate-200">
                      <User className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{selectedCliente.nome}</p>
                      {selectedCliente.cpfCnpj && (
                        <p className="text-sm text-slate-500">{selectedCliente.cpfCnpj}</p>
                      )}
                      {selectedCliente.celular && (
                        <p className="text-sm text-slate-500">{selectedCliente.celular}</p>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowClienteModal(true)}
                    className="text-sm font-semibold text-primary-600 hover:text-primary-700"
                  >
                    Alterar
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowClienteModal(true)}
                className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-4 text-slate-500 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <User className="w-5 h-5" />
                Selecionar Cliente
              </button>
            )}
            {errors.clienteId && (
              <p className="mt-2 text-sm text-rose-600">{errors.clienteId.message}</p>
            )}
          </Panel>
        </div>

        {/* Valores */}
        <Panel title="Valores">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Controller
              name="valorVenda"
              control={control}
              render={({ field }) => (
                <InputDecimal
                  label="Valor do Veículo *"
                  prefix="R$ "
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.valorVenda?.message}
                />
              )}
            />

            <Controller
              name="valorDesconto"
              control={control}
              render={({ field }) => (
                <InputDecimal
                  label="Desconto"
                  prefix="R$ "
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />

            <Controller
              name="valorAcrescimo"
              control={control}
              render={({ field }) => (
                <InputDecimal
                  label="Acréscimo"
                  prefix="R$ "
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Valor Final</label>
              <div className="flex h-10 items-center justify-end rounded-xl border border-emerald-200 bg-emerald-50 px-3">
                <span className="text-lg font-bold tabular-nums text-emerald-600">{formatCurrency(valorFinal)}</span>
              </div>
            </div>
          </div>
        </Panel>

        {/* Veículo de Troca */}
        <Panel title="Veículo de Troca (Opcional)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              {selectedTroca ? (
                <div className="rounded-xl bg-slate-50 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-slate-900">
                        {selectedTroca.marcaNome} {selectedTroca.modeloNome}
                      </p>
                      <p className="text-sm text-slate-500">
                        {selectedTroca.ano}/{selectedTroca.anoModelo}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedTroca(null);
                        setValue('veiculoTrocaId', null);
                        setValue('valorTroca', null);
                      }}
                      className="text-sm font-semibold text-rose-600 hover:text-rose-700"
                    >
                      Remover
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowTrocaModal(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-4 text-slate-500 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <Car className="w-5 h-5" />
                  Adicionar Veículo de Troca
                </button>
              )}
            </div>

            {selectedTroca && (
              <Controller
                name="valorTroca"
                control={control}
                render={({ field }) => (
                  <InputDecimal
                    label="Valor da Troca"
                    prefix="R$ "
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
          </div>
        </Panel>

        {/* Forma de Pagamento */}
        <Panel title="Forma de Pagamento">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Forma de Pagamento</label>
              <select
                {...register('formaPagamento')}
                className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value="AVISTA">À Vista</option>
                <option value="FINANCIAMENTO">Financiamento</option>
                <option value="CONSORCIO">Consórcio</option>
                <option value="CARTAO">Cartão</option>
                <option value="BOLETO">Boleto</option>
                <option value="PIX">PIX</option>
              </select>
            </div>

            {formaPagamento === 'FINANCIAMENTO' && (
              <>
                <Controller
                  name="valorEntrada"
                  control={control}
                  render={({ field }) => (
                    <InputDecimal
                      label="Valor de Entrada"
                      prefix="R$ "
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Parcelas</label>
                  <input
                    type="number"
                    {...register('quantidadeParcelas')}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Financeira</label>
                  <input
                    type="text"
                    {...register('financeira')}
                    placeholder="Nome da financeira"
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                </div>
              </>
            )}
          </div>
        </Panel>

        {/* Comissão e Data */}
        <Panel title="Detalhes da Venda">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Data da Venda</label>
              <input
                type="date"
                {...register('dataVenda')}
                className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <Controller
              name="comissaoPercentual"
              control={control}
              render={({ field }) => (
                <InputDecimal
                  label="Comissão (%)"
                  suffix=" %"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </Panel>

        {/* Observações */}
        <Panel title="Observações">
          <textarea
            {...register('observacoes')}
            rows={3}
            placeholder="Observações sobre a venda..."
            className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </Panel>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/vendas')}
            className={buttonClass('secondary')}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className={buttonClass('primary')}
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isSaving ? 'Salvando...' : 'Registrar Venda'}
          </button>
        </div>
      </form>

      {/* Modal Selecionar Veículo */}
      <Modal
        isOpen={showVeiculoModal}
        onClose={() => setShowVeiculoModal(false)}
        title="Selecionar Veículo"
        size="lg"
      >
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por marca, modelo ou placa..."
              value={buscaVeiculo}
              onChange={(e) => setBuscaVeiculo(e.target.value)}
              className="h-10 w-full rounded-xl border border-slate-200 pl-10 pr-4 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              autoFocus
            />
          </div>

          <div className="max-h-96 overflow-y-auto space-y-2">
            {veiculos.map((veiculo) => (
              <button
                key={veiculo.id}
                type="button"
                onClick={() => handleSelectVeiculo(veiculo)}
                className="w-full rounded-xl border border-slate-200 p-4 text-left transition-colors hover:border-primary-400 hover:bg-primary-50"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-slate-900">
                      {veiculo.marcaNome} {veiculo.modeloNome}
                    </p>
                    <p className="text-sm text-slate-500">
                      {veiculo.versao && `${veiculo.versao} • `}
                      {veiculo.ano}/{veiculo.anoModelo}
                      {veiculo.placa && ` • ${veiculo.placa}`}
                    </p>
                  </div>
                  <span className="font-semibold tabular-nums text-emerald-600">
                    {formatCurrency(veiculo.valorVenda)}
                  </span>
                </div>
              </button>
            ))}
            {veiculos.length === 0 && (
              <p className="py-4 text-center text-sm text-slate-400">Nenhum veículo disponível encontrado</p>
            )}
          </div>
        </div>
      </Modal>

      {/* Modal Selecionar Cliente */}
      <Modal
        isOpen={showClienteModal}
        onClose={() => setShowClienteModal(false)}
        title="Selecionar Cliente"
        size="lg"
      >
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por nome, CPF/CNPJ ou telefone..."
              value={buscaCliente}
              onChange={(e) => setBuscaCliente(e.target.value)}
              className="h-10 w-full rounded-xl border border-slate-200 pl-10 pr-4 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              autoFocus
            />
          </div>

          <div className="max-h-96 overflow-y-auto space-y-2">
            {clientes.map((cliente) => (
              <button
                key={cliente.id}
                type="button"
                onClick={() => handleSelectCliente(cliente)}
                className="w-full rounded-xl border border-slate-200 p-4 text-left transition-colors hover:border-primary-400 hover:bg-primary-50"
              >
                <p className="font-medium text-slate-900">{cliente.nome}</p>
                <p className="text-sm text-slate-500">
                  {cliente.cpfCnpj && `${cliente.cpfCnpj}`}
                  {cliente.cpfCnpj && cliente.celular && ' • '}
                  {cliente.celular && cliente.celular}
                </p>
              </button>
            ))}
            {clientes.length === 0 && (
              <p className="py-4 text-center text-sm text-slate-400">Nenhum cliente encontrado</p>
            )}
          </div>
        </div>
      </Modal>

      {/* Modal Selecionar Veículo de Troca */}
      <Modal
        isOpen={showTrocaModal}
        onClose={() => setShowTrocaModal(false)}
        title="Selecionar Veículo de Troca"
        size="lg"
      >
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por marca, modelo ou placa..."
              value={buscaVeiculo}
              onChange={(e) => setBuscaVeiculo(e.target.value)}
              className="h-10 w-full rounded-xl border border-slate-200 pl-10 pr-4 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              autoFocus
            />
          </div>

          <div className="max-h-96 overflow-y-auto space-y-2">
            {/* Filtrar o veículo já selecionado na venda - não pode ser usado como troca */}
            {veiculos
              .filter((v) => !selectedVeiculo || v.id !== selectedVeiculo.id)
              .map((veiculo) => (
                <button
                  key={veiculo.id}
                  type="button"
                  onClick={() => handleSelectTroca(veiculo)}
                  className="w-full rounded-xl border border-slate-200 p-4 text-left transition-colors hover:border-primary-400 hover:bg-primary-50"
                >
                  <p className="font-medium text-slate-900">
                    {veiculo.marcaNome} {veiculo.modeloNome}
                  </p>
                  <p className="text-sm text-slate-500">
                    {veiculo.versao && `${veiculo.versao} • `}
                    {veiculo.ano}/{veiculo.anoModelo}
                    {veiculo.placa && ` • ${veiculo.placa}`}
                  </p>
                </button>
              ))}
          </div>
        </div>
      </Modal>
    </div>
  );
}
